/**
 * What a put-away may offer for a known destination (a slot, or a unit):
 * the destination's Kept at, resolved from its whole ancestor chain, and the
 * split of lots or materials against it. Pure so it is covered: this repo's
 * vitest only collects `src/**\/*.test.ts`.
 */

import { checkPutAway } from '@/lib/storageEligibility'
import { isOccupiable, pathSegments } from '@/lib/storageTree'
import type { ApiStorageNode, StorageCondition, StorageConditionMeta } from '@/types/storage'

export type DestinationKeptAt =
  | { status: 'set'; condition: StorageCondition }
  /**
   * Nothing in the chain declares one, or what it declares is not somewhere
   * stock rests (a controlled-rate freezer) — put-away needs one set first.
   */
  | { status: 'none' }
  /** An ancestor is not loaded yet and nothing below it decides. */
  | { status: 'unknown' }

/**
 * The Kept at in force at `node`: its own, or the nearest ancestor's.
 *
 * Walks every prefix of the node's path, deepest first, against `nodes` (the
 * lazily loaded tree). Where a prefix is not in hand the answer is
 * `'unknown'`, never "no Kept at" — the gap is exactly what let a slot whose
 * rack had not loaded fall back to null, and a null destination is what sent
 * the put-away queue unfiltered.
 *
 * `conditions` is the reference table; a non-persistent condition found is
 * reported as `'none'`, because the server refuses put-away there.
 */
export function destinationKeptAt(
  node: ApiStorageNode,
  nodes: ApiStorageNode[],
  conditions: StorageConditionMeta[] = [],
): DestinationKeptAt {
  const found = (condition: StorageCondition): DestinationKeptAt =>
    usableKeptAt(condition, conditions) ? { status: 'set', condition } : { status: 'none' }

  if (node.providesCondition) return found(node.providesCondition)

  const byPath = new Map(nodes.map((n) => [n.path, n]))
  const segments = pathSegments(node.path)
  for (let i = segments.length - 1; i > 0; i--) {
    const ancestor = byPath.get(segments.slice(0, i).join('/'))
    if (!ancestor) return { status: 'unknown' }
    if (ancestor.providesCondition) return found(ancestor.providesCondition)
  }
  return { status: 'none' }
}

/**
 * A destination condition stock can be put away at, or null. A condition the
 * reference marks non-persistent (product passes through, never rests) is
 * null; one the reference has not loaded yet is kept — the server still
 * checks.
 */
export function usableKeptAt(
  condition: StorageCondition | null,
  conditions: StorageConditionMeta[],
): StorageCondition | null {
  if (!condition) return null
  return conditions.find((c) => c.name === condition)?.persistent === false ? null : condition
}

/** `GET …/unplaced` returns at most this many lots (`take: 100`, by lot number). */
export const UNPLACED_PAGE_LIMIT = 100

/** A read that came back at the server's cap may be missing lots past it. */
export function isTruncated(rows: unknown[] | undefined): boolean {
  return (rows?.length ?? 0) >= UNPLACED_PAGE_LIMIT
}

/** Said under a list that may be cut short by the cap. */
export const TRUNCATED_MESSAGE =
  "Showing the first 100 waiting lots. If yours isn't here, set its location from the lot on its Inventory page."

/**
 * Split lots or materials against a destination's Kept at: the ones that
 * need exactly it, the ones whose master data states nothing (legal anywhere,
 * shown apart), and a count of the ones needing a different temperature —
 * which are never offered.
 */
export function splitByKeptAt<T extends { requiresCondition?: StorageCondition | null }>(
  items: T[],
  condition: StorageCondition,
): { matching: T[]; unstated: T[]; otherCount: number } {
  const matching: T[] = []
  const unstated: T[] = []
  let otherCount = 0
  for (const item of items) {
    const required = item.requiresCondition ?? null
    if (required == null) unstated.push(item)
    else if (required === condition) matching.push(item)
    else otherCount++
  }
  return { matching, unstated, otherCount }
}

/**
 * The slots a lot of a material needing `required` may be moved into — the
 * other direction of the put-away filter, where the LOT is fixed and the
 * places vary (Set location).
 *
 * Each slot's Kept at comes from its whole chain in `nodes` (the unit's
 * subtree), so a rack kept at another temperature drops the slots under it.
 * The test is `checkPutAway`'s, the server's rule: exact match, and a
 * non-persistent or missing Kept at refuses. A slot whose chain is not loaded
 * is left out rather than guessed. No requirement: every slot passes.
 */
export function slotsForRequirement<T extends ApiStorageNode>(
  slots: T[],
  nodes: ApiStorageNode[],
  required: StorageCondition | null,
  conditions: StorageConditionMeta[],
): T[] {
  if (required == null) return slots
  return slots.filter((slot) => {
    const keptAt = destinationKeptAt(slot, nodes, conditions)
    return keptAt.status === 'set' && checkPutAway(required, keptAt.condition, conditions).ok
  })
}

/**
 * The slot choices for moving a lot (Set location), from the unit's subtree.
 *
 * A slot must be a usable slot — a position, not the holding area, not
 * blocked — free, and kept at what the material needs. The lot's CURRENT slot
 * bypasses only "free" and the temperature, so "no change" stays expressible
 * even where it no longer matches; it is never counted in `freeCount`.
 */
export function slotOptionsFor(
  nodes: ApiStorageNode[],
  required: StorageCondition | null,
  conditions: StorageConditionMeta[],
  currentNodeId: string | null | undefined,
): { options: ApiStorageNode[]; freeCount: number } {
  const usable = nodes.filter((n) => isOccupiable(n.kind) && !n.isHoldingArea && !n.blocked)
  const free = slotsForRequirement(
    usable.filter((n) => !n.occupied && n.id !== currentNodeId),
    nodes,
    required,
    conditions,
  )
  const current = usable.find((n) => n.id === currentNodeId)
  return { options: current ? [...free, current] : free, freeCount: free.length }
}

/** "Showing stock kept at ≤ −150 °C · Cryogenic". */
export function keptAtFilterText(condition: StorageCondition, conditions: StorageConditionMeta[]): string {
  const meta = conditions.find((c) => c.name === condition)
  if (!meta) return `Showing stock kept at ${condition}`
  return meta.temperature
    ? `Showing stock kept at ${meta.temperature} · ${meta.label}`
    : `Showing stock kept at ${meta.label}`
}

/** What a destination with no Kept at says instead of a list. */
export const NO_KEPT_AT_MESSAGE = 'This place has no Kept at set — set one in Settings to put stock here'

/** The collapsed group's label for items whose master data states no temperature. */
export const UNSTATED_LABEL = 'Temperature not stated'
